import { FC, useEffect, useMemo } from 'react'
import { Col, Form, InputNumber, Select } from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { skipToken } from '@reduxjs/toolkit/query'
import MaterialTypesGrouped from '../../../../../../../../components/form-items/material-types-grouped/material-types-grouped'
import AutoComplete from '../../../../../../../../components/custom-form-controls/auto-complete'
import {
    requiredInputNumberRules,
    requiredOneElementRules,
} from '../../../../../../../../constants/forms/validation/rules'
import { useGetMaterialTypesQuery } from '../../../../../../../../store/api/material-types-api'
import { useGetStocksQuery } from '../../../../../../../../store/api/stocks-api'
import { useGetUsersQuery } from '../../../../../../../../store/api/users-api'
import { useUser } from '../../../../../../../../hooks/use-user'
import useMaterialWidthOptions from '../../../../../../../../helpers/render/use-material-width-options'
import calculateStockAmount from '../../../../../../../../helpers/entities/stock/calculate-stock-amount'
import { createValidateMaterialWidth } from '../../../../../../material-order-requests/components/material-order-requests/helpers/parse-material-width-value'
import MaterialType from '../../../../../../../../types/api/material-type'

interface ReceiptPositionFieldsProps {
    name: number
}

const ReceiptPositionFields: FC<ReceiptPositionFieldsProps> = ({ name }) => {
    const form = Form.useFormInstance()
    const user = useUser()
    const materialTypeDocId = useWatch(['positions', name, 'materialTypeDocId'], form)

    const { data: stocksData } = useGetStocksQuery(
        materialTypeDocId
            ? {
                  filters: {
                      material: {
                          material_type: { documentId: { $eq: materialTypeDocId } },
                      },
                  },
                  populate: '*',
                  pagination: { page: 1, pageSize: 1000 },
              }
            : skipToken
    )
    const { data: materialTypeData } = useGetMaterialTypesQuery(
        materialTypeDocId
            ? {
                  filters: { documentId: { $eq: materialTypeDocId } },
                  populate: { unit: true },
              }
            : skipToken
    )
    const { data: users } = useGetUsersQuery()

    const materialOptions = useMaterialWidthOptions({
        items: stocksData?.data,
        getWidth: item => item.material.width,
        getDocumentId: item => item.material.documentId,
        getStock: item => calculateStockAmount(item),
    })

    const materials = useMemo(() => stocksData?.data.map(item => item.material) || [], [stocksData])

    const validateMaterialWidth = useMemo(
        () => createValidateMaterialWidth(materialOptions, materials),
        [materialOptions, materials]
    )

    const selectedMaterialType = materialTypeData?.data[0] as MaterialType | undefined
    const unitShortName = selectedMaterialType?.unit?.shortName

    useEffect(() => {
        form.setFieldValue(['positions', name, 'material'], undefined)
    }, [form, materialTypeDocId, name])

    useEffect(() => {
        const unitDocumentId = selectedMaterialType?.unit?.documentId
        if (unitDocumentId) {
            form.setFieldValue(['positions', name, 'unitDocumentId'], unitDocumentId)
        }
    }, [form, name, selectedMaterialType?.unit?.documentId])

    return (
        <>
            <Col sm={6}>
                <MaterialTypesGrouped
                    name={[name, 'materialTypeDocId']}
                    rules={requiredOneElementRules}
                    label={'Тип материала'}
                    purchaserDocumentId={user?.documentId}
                />
            </Col>
            <Col sm={4}>
                <Form.Item
                    name={[name, 'material']}
                    label={'Ширина'}
                    rules={[...requiredOneElementRules, { validator: validateMaterialWidth }]}
                >
                    <AutoComplete
                        options={materialOptions}
                        placeholder='Выберите или введите ширину'
                        disabled={!materialTypeDocId}
                        allowClear
                        style={{ width: '100%' }}
                        dropdownStyle={{ minWidth: '250px' }}
                        getOptionKey={opt => opt.key}
                    />
                </Form.Item>
            </Col>
            <Col sm={4}>
                <Form.Item
                    name={[name, 'amount']}
                    label={unitShortName ? `Кол-во, ${unitShortName}` : 'Кол-во'}
                    rules={requiredInputNumberRules}
                >
                    <InputNumber min={0.1} style={{ width: '100%' }} />
                </Form.Item>
            </Col>
            <Col sm={6}>
                <Form.Item
                    name={[name, 'initiator']}
                    label={'Инициатор'}
                    rules={requiredOneElementRules}
                >
                    <Select
                        showSearch
                        optionFilterProp='label'
                        options={users?.map(item => ({
                            value: item.documentId,
                            label: item.username,
                        }))}
                    />
                </Form.Item>
            </Col>
            <Form.Item name={[name, 'unitDocumentId']} hidden>
                <input type='hidden' />
            </Form.Item>
        </>
    )
}

export default ReceiptPositionFields
